import { useEffect, useRef, useState } from "react";

import { downloadTable } from "./table";
import type { TableData, TableFormat } from "./table";

/**
 * "↓ CSV / Excel ▾" over a table: every row, in the table's current order,
 * as a .csv or an .xlsx. `data` is a function so the rows are only gathered
 * when someone asks for the file.
 */
export function TableExportMenu({
  data,
  name,
  sheet,
  label = "Export",
}: {
  data: () => TableData;
  /** Parts of the filename, slugged and dated: ["trades", "BTCUSDT", "4h"]. */
  name: (string | null | undefined)[];
  /** The Excel sheet's tab; the first name part when not given. */
  sheet?: string;
  label?: string;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent | KeyboardEvent) => {
      if (e instanceof KeyboardEvent ? e.key === "Escape" : !ref.current?.contains(e.target as Node))
        setOpen(false);
    };
    window.addEventListener("mousedown", close);
    window.addEventListener("keydown", close);
    return () => {
      window.removeEventListener("mousedown", close);
      window.removeEventListener("keydown", close);
    };
  }, [open]);

  function save(format: TableFormat) {
    setOpen(false);
    downloadTable(format, data(), { name, sheet });
  }

  return (
    <div className="export-menu table-export" ref={ref}>
      <button
        type="button"
        className="btn btn--sm btn--ghost"
        aria-haspopup="menu"
        aria-expanded={open}
        title="Download this table as CSV or Excel"
        onClick={() => setOpen((v) => !v)}
      >
        ↓ {label} <span aria-hidden="true">▾</span>
      </button>
      {open && (
        <div className="export-menu__list table-export__list" role="menu">
          <button type="button" role="menuitem" onClick={() => save("csv")}>
            <strong>CSV</strong> <code>.csv</code>
            <span className="muted small">Plain text, opens anywhere.</span>
          </button>
          <button type="button" role="menuitem" onClick={() => save("xlsx")}>
            <strong>Excel</strong> <code>.xlsx</code>
            <span className="muted small">A spreadsheet with typed numbers and dates.</span>
          </button>
          <p className="export-menu__note muted small">Every row, in the order the table is sorted.</p>
        </div>
      )}
    </div>
  );
}

/** A right-aligned strip above a table for its column picker and export menu. */
export function TableTools({ children }: { children: React.ReactNode }) {
  return <div className="table-tools">{children}</div>;
}
